/**
 * PlatformGuard — keeps every member inside the app tree they belong to.
 *
 *   - A standalone product workspace (Sales, Clients, SOW Desk) has no owner
 *     dashboard: every member, owner included, is pinned to the workspace's
 *     home product.
 *   - In a business workspace, a member/viewer whose department sets a
 *     platform is pinned to that platform's tree. Owners/admins roam freely
 *     (and get the PlatformSwitcher).
 *
 * The rules are pure functions in lib/platform-pin.ts (tested there); this
 * component only wires them to the router. Public + /preview routes are
 * untouched.
 */
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMe } from '../lib/hooks/use-me';
import { resolvePin } from '../lib/platform-pin';

export function PlatformGuard() {
  const me = useMe();
  const location = useLocation();
  const navigate = useNavigate();

  const target = resolvePin(
    {
      userId: me.user.id,
      role: me.role,
      workspaceKind: me.workspaceKind,
      homePath: me.homePath,
      assignedPlatform: me.assignedPlatform,
      features: me.features,
    },
    location.pathname,
  );

  useEffect(() => {
    if (target) navigate(target, { replace: true });
  }, [target, navigate]);

  return null;
}
